import React from 'react';
import { flushSync } from 'react-dom';
import {
  alpha,
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';

import pingwinMark from '../../assets/logos/pingwin-mark.png';
import PinguAssistantMessage from './PinguAssistantMessage';
import PinguWaitingIndicator from './PinguWaitingIndicator';
import { ChatMessage, ChatMessageFeedbackInput } from './types';
import panelModes, { PanelMode } from './panelModes';
import useReservedResponseSpace, { USER_MESSAGE_SCROLL_MARGIN_PX } from './useReservedResponseSpace';

const CONTENT_HIDDEN_BELOW_THRESHOLD_PX = 48;

const EMPTY_STATE_MARK_SIZE: Record<PanelMode, number> = {
  [panelModes.FLOATING]: 72,
  [panelModes.DOCKED]: 96,
};

// Space kept clear above and below the large mark, inside the transcript's own padding.
const EMPTY_STATE_MARK_PADDING_PX = 8;

const largeEmptyStateMarkFits = (availableHeight: number, mode: PanelMode) => (
  availableHeight >= EMPTY_STATE_MARK_SIZE[mode] + 2 * EMPTY_STATE_MARK_PADDING_PX
);

const PinguMessageList = ({
  messages,
  loading,
  activeSessionId,
  onMessageTyped,
  onMessageFeedback,
  onWithdrawFeedback,
  onRetry,
  onCheckAgain,
  mode = panelModes.FLOATING,
  onEmptyStateMarkFitChange,
}: {
  messages: ChatMessage[];
  loading: boolean;
  activeSessionId: string | null;
  onMessageTyped: (index: number) => void;
  onMessageFeedback: (
    messageIndex: number,
    feedback: ChatMessageFeedbackInput,
  ) => Promise<boolean>;
  onWithdrawFeedback: (messageIndex: number) => Promise<boolean>;
  onRetry: () => void;
  onCheckAgain: () => void;
  mode?: PanelMode;
  // Told whether the large mark fits whole, so a small one can be drawn elsewhere instead.
  onEmptyStateMarkFitChange?: (fits: boolean) => void;
}) => {
  const {
    scrollContainerRef,
    latestUserMessageRef,
    reservedSpace,
  } = useReservedResponseSpace({ messages, loading, activeSessionId });
  const [contentHiddenBelow, setContentHiddenBelow] = React.useState(false);

  const updateContentHiddenBelow = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const distanceFromBottom = container.scrollHeight - container.scrollTop
      - container.clientHeight;
    setContentHiddenBelow(distanceFromBottom > CONTENT_HIDDEN_BELOW_THRESHOLD_PX);
  }, [scrollContainerRef]);

  // Answers grow while they are typed out, so the scroll position alone does not
  // say when content starts to run below the fold.
  React.useEffect(() => {
    updateContentHiddenBelow();
    const container = scrollContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(updateContentHiddenBelow);
    observer.observe(container);
    Array.from(container.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [messages, loading, reservedSpace, scrollContainerRef, updateContentHiddenBelow]);

  const empty = messages.length === 0;
  const [emptyStateMarkFits, setEmptyStateMarkFits] = React.useState(true);
  const onEmptyStateMarkFitChangeRef = React.useRef(onEmptyStateMarkFitChange);
  onEmptyStateMarkFitChangeRef.current = onEmptyStateMarkFitChange;

  // The transcript is the only part of the panel that shrinks, so a mark drawn at a
  // fixed size would be clipped once it no longer fits. It is left out instead.
  // The list and the window are told in the same update, before the browser paints,
  // so there is never a frame with no mark or with both.
  React.useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!empty || !container) return undefined;

    const report = () => {
      const fits = largeEmptyStateMarkFits(container.clientHeight, mode);
      setEmptyStateMarkFits(fits);
      onEmptyStateMarkFitChangeRef.current?.(fits);
    };
    // Updates made in a layout effect are already flushed before paint.
    report();
    if (typeof ResizeObserver === 'undefined') return undefined;

    // A resize is observed after layout but before paint. Without flushSync React
    // would render the change in a later task, after the stale frame was painted.
    const observer = new ResizeObserver(() => flushSync(report));
    observer.observe(container);
    return () => observer.disconnect();
  }, [empty, mode, scrollContainerRef]);

  const latestUserMessageIndex = messages.findLastIndex(({ role }) => role === 'user');
  const latestAnswerIndex = messages.findLastIndex(
    ({
      role,
      emptyReply,
      error,
      connectionLost,
    }) => (
      role === 'assistant' && !emptyReply && !error && !connectionLost
    ),
  );

  return (
    <Stack
      ref={scrollContainerRef}
      spacing={4}
      useFlexGap
      onScroll={updateContentHiddenBelow}
      sx={(theme) => ({
        p: 4,
        pb: `calc(${theme.spacing(4)} + ${reservedSpace}px)`,
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        borderBottom: 1,
        borderColor: contentHiddenBelow ? 'divider' : 'transparent',
        scrollbarWidth: 'thin',
        scrollbarColor: `${alpha(theme.palette.emphasisText.main, 0.5)} transparent`,
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: alpha(theme.palette.emphasisText.main, 0.5),
          borderRadius: 3,
        },
      })}
    >
      {empty ? emptyStateMarkFits && (
        <Box sx={{
          flex: 1,
          // Spans the transcript's padding too, so the mark fits at the default panel height.
          my: -4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        >
          <Box
            component="img"
            src={pingwinMark}
            alt=""
            sx={{
              width: EMPTY_STATE_MARK_SIZE[mode],
              height: EMPTY_STATE_MARK_SIZE[mode],
            }}
          />
        </Box>
      ) : (
        messages.map((message, index) => {
          if (message.emptyReply) {
            return (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={`empty-${index}`}
                sx={{ alignSelf: 'stretch' }}
              >
                <Stack spacing={1}>
                  <Typography variant="reading" component="p" color="text.primary">
                    Pingwin didn&apos;t reach an answer this time.
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Try rephrasing, or asking about something narrower.
                  </Typography>
                </Stack>
              </Box>
            );
          }

          if (message.error) {
            return (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={`error-${index}`}
                role="alert"
                sx={{
                  alignSelf: 'stretch',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                }}
              >
                <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main', mt: 0.5 }} />
                <Box>
                  <Typography variant="body1" color="text.primary">{message.content}</Typography>
                  {index === messages.length - 1 && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={onRetry}
                      disabled={loading}
                      sx={{ mt: 2 }}
                    >
                      Try again
                    </Button>
                  )}
                </Box>
              </Box>
            );
          }

          if (message.connectionLost) {
            return (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={`connection-lost-${index}`}
                role="status"
                sx={{
                  alignSelf: 'stretch',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                }}
              >
                <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.5 }} />
                <Box>
                  <Typography variant="body1" color="text.primary">{message.content}</Typography>
                  {index === messages.length - 1 && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={onCheckAgain}
                      disabled={loading}
                      sx={{ mt: 2 }}
                    >
                      Check again
                    </Button>
                  )}
                </Box>
              </Box>
            );
          }

          if (message.role === 'user') {
            return (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={`user-${index}`}
                ref={index === latestUserMessageIndex ? latestUserMessageRef : undefined}
                sx={{
                  alignSelf: 'flex-end',
                  maxWidth: '85%',
                  px: 3,
                  py: 2,
                  scrollMarginTop: `${USER_MESSAGE_SCROLL_MARGIN_PX}px`,
                  borderRadius: 1.5,
                  backgroundColor: 'neutral.main',
                  color: 'text.primary',
                  overflowWrap: 'anywhere',
                }}
              >
                {message.content && (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.content}
                  </Typography>
                )}
                {message.attachments?.map((attachment, attachmentIndex) => (
                  <Stack
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${attachment.id || attachment.name}-${attachmentIndex}`}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: message.content || attachmentIndex ? 1 : 0, minWidth: 0 }}
                  >
                    <InsertDriveFileOutlinedIcon aria-hidden sx={{ fontSize: 18, flexShrink: 0 }} />
                    <Typography variant="body1" noWrap title={attachment.name}>
                      {attachment.name}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            );
          }

          return (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={`assistant-${index}`}
              sx={{
                alignSelf: 'stretch',
                maxWidth: '85%',
                color: 'text.primary',
                overflowWrap: 'anywhere',
                '&:hover .pinguFeedbackRow, &:focus-within .pinguFeedbackRow': { opacity: 1 },
                '@media (hover: none)': { '& .pinguFeedbackRow': { opacity: 1 } },
              }}
            >
              <PinguAssistantMessage
                message={message}
                index={index}
                pinned={index === latestAnswerIndex && !message.justReceived}
                onTyped={onMessageTyped}
                onMessageFeedback={onMessageFeedback}
                onWithdrawFeedback={onWithdrawFeedback}
              />
            </Box>
          );
        })
      )}
      {loading && (
        <Box sx={{ alignSelf: 'stretch' }}>
          <PinguWaitingIndicator />
        </Box>
      )}
    </Stack>
  );
};

export default PinguMessageList;
